import React, { useState, useRef } from 'react';
import { DB, uploadImage } from '../../data/database';

const INK = '#14161C';
const INK2 = '#52545C';
const INK3 = '#8B8D94';
const INK4 = '#B0B2B8';
const LINE = '#E5E5E3';
const BLUE = '#2563EB';
const GREEN = '#22C55E';
const PAPER = '#FAFAF8';
const heading = '"Inter Tight", sans-serif';
const mono = '"JetBrains Mono", monospace';

const PUBLIC_SIGNUP_ENABLED = false;

const COUNTRIES = ['UAE', 'Saudi Arabia', 'Kuwait', 'Bahrain', 'Qatar', 'Oman', 'Kazakhstan', 'Russia', 'Georgia', 'Azerbaijan', 'Kenya', 'Tanzania', 'Uganda', 'Pakistan', 'India', 'Turkey', 'Other'];
const EMIRATES = ['Abu Dhabi', 'Dubai', 'Sharjah', 'Ajman', 'Ras Al Khaimah', 'Fujairah', 'Umm Al Quwain'];

/* ── Progress Bar ── */
function ProgressBar({ step, type }) {
  const steps = type === 'individual'
    ? [{ n: 1, label: 'Account type' }, { n: 2, label: 'Your details' }, { n: 4, label: 'Review' }]
    : [{ n: 1, label: 'Account type' }, { n: 2, label: 'Your details' }, { n: 3, label: 'Verification' }, { n: 4, label: 'Review' }];

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0, marginBottom: 40, flexWrap: 'wrap' }}>
      {steps.map((s, i) => {
        const done = step > s.n;
        const active = step === s.n;
        return (
          <React.Fragment key={s.n}>
            {i > 0 && <div style={{ width: 60, height: 2, background: done ? GREEN : LINE, margin: '0 4px' }} />}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{
                width: 28, height: 28, borderRadius: '50%', display: 'grid', placeItems: 'center',
                fontSize: 12, fontWeight: 700, fontFamily: mono,
                background: done ? GREEN : active ? INK : 'transparent',
                color: done || active ? '#fff' : INK4,
                border: done || active ? 'none' : `2px solid ${LINE}`,
              }}>
                {done ? '✓' : s.n}
              </div>
              <span style={{ fontSize: 12, fontWeight: active ? 600 : 400, color: active ? INK : INK3, fontFamily: heading, whiteSpace: 'nowrap' }}>{s.label}</span>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
}

/* ── Shared input styles ── */
const inputStyle = { width: '100%', height: 48, padding: '0 16px', borderRadius: 10, border: `1px solid ${LINE}`, background: '#fff', fontFamily: heading, fontSize: 14, color: INK, outline: 'none', boxSizing: 'border-box' };
const labelStyle = { fontSize: 13, fontWeight: 600, color: INK, marginBottom: 6, display: 'block' };
const errStyle = { fontSize: 11, color: '#DC2626', marginTop: 3 };

function WebsiteRegister({ onNavigate, onGoToLogin }) {
  const [step, setStep] = useState(1);
  const [type, setType] = useState(''); // 'individual' or 'trade'
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  if (!PUBLIC_SIGNUP_ENABLED) {
    return (
      <div style={{ maxWidth: 600, margin: '0 auto', padding: '80px 24px', textAlign: 'center' }}>
        <div style={{ fontSize: 48, marginBottom: 20 }}>🔒</div>
        <h1 style={{ fontFamily: heading, fontSize: 32, fontWeight: 700, color: INK, marginBottom: 12 }}>
          Registration is currently closed
        </h1>
        <p style={{ fontSize: 15, color: INK3, lineHeight: 1.7, maxWidth: 440, margin: '0 auto 32px' }}>
          New accounts are created by our team. Contact us to get started, or sign in if you already have an account.
        </p>
        <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
          <button onClick={() => onGoToLogin ? onGoToLogin() : onNavigate('website-login')} style={{ padding: '14px 28px', borderRadius: 12, background: INK, color: '#fff', fontFamily: heading, fontSize: 15, fontWeight: 600, border: 'none', cursor: 'pointer' }}>
            Sign in →
          </button>
          <button onClick={() => onNavigate('website-contact')} style={{ padding: '14px 28px', borderRadius: 12, background: '#fff', color: INK, fontFamily: heading, fontSize: 15, fontWeight: 600, border: `1px solid ${LINE}`, cursor: 'pointer' }}>
            Contact us
          </button>
        </div>
      </div>
    );
  }

  // Individual fields
  const [ind, setInd] = useState({ name: '', phone: '', email: '', country: '', city: '', password: '' });

  // Trade fields
  const [trade, setTrade] = useState({
    company: '', license: '', emirate: '', activity: '',
    signatory: '', position: '', email: '', phone: '', password: '',
  });

  // Documents
  const [docs, setDocs] = useState([]);
  const [termsAgreed, setTermsAgreed] = useState(false);
  const fileRefs = useRef({});

  const setI = (k, v) => { setInd(p => ({ ...p, [k]: v })); setErrors(p => ({ ...p, [k]: '' })); };
  const setT = (k, v) => { setTrade(p => ({ ...p, [k]: v })); setErrors(p => ({ ...p, [k]: '' })); };

  /* ── Validation ── */
  const validateStep2 = () => {
    const e = {};
    if (type === 'individual') {
      if (!ind.name.trim()) e.name = 'Name is required';
      if (!ind.email.trim() || !ind.email.includes('@')) e.email = 'Valid email required';
      if (!ind.phone.trim()) e.phone = 'Phone is required';
      if (ind.password.length < 8) e.password = 'Minimum 8 characters';
    } else {
      if (!trade.company.trim()) e.company = 'Company name required';
      if (!trade.license.trim()) e.license = 'License number required';
      if (!trade.signatory.trim()) e.signatory = 'Name required';
      if (!trade.email.trim() || !trade.email.includes('@')) e.email = 'Valid email required';
      if (!trade.phone.trim()) e.phone = 'Phone required';
      if (trade.password.length < 8) e.password = 'Minimum 8 characters';
    }
    // Check duplicate email
    const email = type === 'individual' ? ind.email : trade.email;
    const customers = DB.get('customers') || [];
    if (customers.some(c => c.email === email)) e.email = 'This email is already registered';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const validateStep3 = () => {
    const required = docs.filter(d => d.type === 'trade_license' || d.type === 'emirates_id');
    if (required.length < 2) { alert('Trade license and Emirates ID are required.'); return false; }
    if (!termsAgreed) { alert('Please agree to the Terms of Use and Privacy Policy.'); return false; }
    return true;
  };

  /* ── Document upload ── */
  const handleDocUpload = async (docType, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { alert('Max file size is 5MB.'); return; }
    try {
      const reader = new FileReader();
      reader.onload = async (ev) => {
        const url = await uploadImage(ev.target.result, 'registration-documents');
        setDocs(prev => [...prev.filter(d => d.type !== docType), { type: docType, name: file.name, url: url || ev.target.result, uploaded_at: new Date().toISOString() }]);
      };
      reader.readAsDataURL(file);
    } catch {}
    e.target.value = '';
  };

  /* ── Submit ── */
  const handleSubmit = () => {
    if (!PUBLIC_SIGNUP_ENABLED) return;
    setSubmitting(true);
    const customers = DB.get('customers') || [];
    const id = DB.genId('CUS');

    if (type === 'individual') {
      const newCust = {
        id, name: ind.name.trim(), email: ind.email.trim(), phone: ind.phone.trim(),
        country: ind.country, city: ind.city, password: ind.password,
        account_type: 'individual', account_status: 'pending_verification', status: 'pending',
        is_walk_in: false, terms_accepted_at: new Date().toISOString(),
        createdAt: new Date().toISOString(),
      };
      DB.set('customers', [...customers, newCust]);

      // Create application for admin approval
      const apps = DB.get('tradeApplications') || [];
      apps.push({
        id: 'TA-' + Date.now().toString(36) + Math.random().toString(36).slice(2, 6),
        business_name: ind.name.trim(), business_country: ind.country || '',
        business_city: ind.city || '', business_type: 'individual',
        contact_name: ind.name.trim(), contact_email: ind.email.trim(),
        contact_phone: ind.phone.trim(), contact_position: '',
        documents: [], notes: 'Individual buyer registration', status: 'pending',
        customer_id: id, created_at: new Date().toISOString(), updated_at: new Date().toISOString(),
      });
      DB.set('tradeApplications', apps);
    } else {
      const newCust = {
        id, name: trade.company.trim(), company: trade.company.trim(),
        email: trade.email.trim(), phone: trade.phone.trim(), password: trade.password,
        trade_license_number: trade.license.trim(), business_activity: trade.activity,
        emirate: trade.emirate, signatory_name: trade.signatory.trim(),
        signatory_position: trade.position, registration_documents: docs,
        account_type: 'trade_dealer', account_status: 'pending_verification', status: 'pending',
        is_walk_in: false, is_trade: true, terms_accepted_at: new Date().toISOString(),
        createdAt: new Date().toISOString(),
      };
      DB.set('customers', [...customers, newCust]);

      // Also create trade application for admin review
      const apps = DB.get('tradeApplications') || [];
      apps.push({
        id: 'TA-' + Date.now().toString(36) + Math.random().toString(36).slice(2, 6),
        business_name: trade.company.trim(), business_country: 'UAE',
        business_city: trade.emirate, business_type: 'dealer',
        contact_name: trade.signatory.trim(), contact_email: trade.email.trim(),
        contact_phone: trade.phone.trim(), contact_position: trade.position,
        license_type: 'Trade License', license_number: trade.license.trim(),
        documents: docs, notes: trade.activity, status: 'pending',
        customer_id: id, created_at: new Date().toISOString(), updated_at: new Date().toISOString(),
      });
      DB.set('tradeApplications', apps);
    }
    setSubmitting(false);
    setDone(true);
  };

  /* ── Navigation ── */
  const next = () => {
    if (step === 1 && !type) return;
    if (step === 2 && !validateStep2()) return;
    if (step === 3 && !validateStep3()) return;
    if (type === 'individual' && step === 2) { setStep(4); return; } // skip step 3
    setStep(s => s + 1);
  };
  const back = () => {
    if (type === 'individual' && step === 4) { setStep(2); return; }
    setStep(s => s - 1);
  };

  /* ── Success page ── */
  if (done) {
    return (
      <div style={{ maxWidth: 600, margin: '0 auto', padding: '60px 24px 80px', textAlign: 'center' }}>
        <div style={{ fontSize: 56, marginBottom: 20 }}>📨</div>
        <h1 style={{ fontFamily: heading, fontSize: 32, fontWeight: 700, color: INK, marginBottom: 12 }}>
          Application submitted!
        </h1>
        <p style={{ fontSize: 15, color: INK3, lineHeight: 1.7, maxWidth: 440, margin: '0 auto 32px' }}>
          {type === 'individual'
            ? 'Your account is under review. Our team will verify and activate your account shortly. You\'ll receive an email once approved.'
            : 'We\'ll review your documents within 24 hours. You\'ll receive an email once your trade account is activated.'}
        </p>
        <button onClick={() => onGoToLogin ? onGoToLogin() : onNavigate('website-login')} style={{ padding: '14px 32px', borderRadius: 12, background: INK, color: '#fff', fontFamily: heading, fontSize: 15, fontWeight: 600, border: 'none', cursor: 'pointer' }}>
          Go to sign in →
        </button>
      </div>
    );
  }

  /* ── Step renderers ── */
  const renderStep1 = () => (
    <div style={{ maxWidth: 640, margin: '0 auto' }}>
      <h2 style={{ fontFamily: heading, fontSize: 28, fontWeight: 700, color: INK, marginBottom: 28 }}>What kind of account are you opening?</h2>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        {[
          { id: 'individual', icon: '👤', title: 'Individual buyer', desc: 'Private buyer in the UAE. Browse stock, save favourites, send inquiries.' },
          { id: 'trade', icon: '🏢', title: 'Business', desc: 'Verified by trade license. Net pricing, bulk inquiries, export documentation.' },
        ].map(opt => (
          <div key={opt.id} onClick={() => setType(opt.id)} style={{
            padding: 24, borderRadius: 14, cursor: 'pointer', transition: 'all 0.2s',
            background: type === opt.id ? INK : '#fff',
            color: type === opt.id ? '#fff' : INK,
            border: `2px solid ${type === opt.id ? INK : LINE}`,
          }}>
            <div style={{ fontSize: 28, marginBottom: 16, opacity: type === opt.id ? 1 : 0.5 }}>{opt.icon}</div>
            <div style={{ fontFamily: heading, fontSize: 17, fontWeight: 600, marginBottom: 8 }}>{opt.title}</div>
            <div style={{ fontSize: 13, color: type === opt.id ? 'rgba(255,255,255,0.6)' : INK3, lineHeight: 1.5 }}>{opt.desc}</div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderStep2Individual = () => (
    <div style={{ maxWidth: 560, margin: '0 auto' }}>
      <h2 style={{ fontFamily: heading, fontSize: 28, fontWeight: 700, color: INK, marginBottom: 28 }}>Your details</h2>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
        <div>
          <label style={labelStyle}>Full name</label>
          <input value={ind.name} onChange={e => setI('name', e.target.value)} style={inputStyle} placeholder="Your full name" />
          {errors.name && <div style={errStyle}>{errors.name}</div>}
        </div>
        <div>
          <label style={labelStyle}>Phone</label>
          <input value={ind.phone} onChange={e => setI('phone', e.target.value)} style={inputStyle} placeholder="+971 50 XXX XXXX" />
          {errors.phone && <div style={errStyle}>{errors.phone}</div>}
        </div>
      </div>
      <div style={{ marginBottom: 16 }}>
        <label style={labelStyle}>Email</label>
        <input value={ind.email} onChange={e => setI('email', e.target.value)} style={inputStyle} placeholder="you@email.com" type="email" />
        {errors.email && <div style={errStyle}>{errors.email}</div>}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
        <div>
          <label style={labelStyle}>Country</label>
          <select value={ind.country} onChange={e => setI('country', e.target.value)} style={{ ...inputStyle, cursor: 'pointer' }}>
            <option value="">Select</option>
            {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <label style={labelStyle}>City</label>
          <input value={ind.city} onChange={e => setI('city', e.target.value)} style={inputStyle} placeholder="City" />
        </div>
      </div>
      <div>
        <label style={labelStyle}>Password</label>
        <input value={ind.password} onChange={e => setI('password', e.target.value)} style={inputStyle} placeholder="At least 8 characters" type="password" />
        <div style={{ fontSize: 11, color: INK4, marginTop: 4 }}>Use a mix of letters, numbers and symbols.</div>
        {errors.password && <div style={errStyle}>{errors.password}</div>}
      </div>
    </div>
  );

  const renderStep2Trade = () => (
    <div style={{ maxWidth: 560, margin: '0 auto' }}>
      <h2 style={{ fontFamily: heading, fontSize: 28, fontWeight: 700, color: INK, marginBottom: 28 }}>Company details</h2>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
        <div>
          <label style={labelStyle}>Company / Trade name</label>
          <input value={trade.company} onChange={e => setT('company', e.target.value)} style={inputStyle} placeholder="Al Mansouri Motors LLC" />
          {errors.company && <div style={errStyle}>{errors.company}</div>}
        </div>
        <div>
          <label style={labelStyle}>Trade license number</label>
          <input value={trade.license} onChange={e => setT('license', e.target.value)} style={inputStyle} placeholder="123456" />
          {errors.license && <div style={errStyle}>{errors.license}</div>}
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
        <div>
          <label style={labelStyle}>Emirate of registration</label>
          <select value={trade.emirate} onChange={e => setT('emirate', e.target.value)} style={{ ...inputStyle, cursor: 'pointer' }}>
            <option value="">Select</option>
            {EMIRATES.map(e => <option key={e} value={e}>{e}</option>)}
          </select>
        </div>
        <div>
          <label style={labelStyle}>Business activity</label>
          <input value={trade.activity} onChange={e => setT('activity', e.target.value)} style={inputStyle} placeholder="Used cars dealer" />
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
        <div>
          <label style={labelStyle}>Authorised signatory name</label>
          <input value={trade.signatory} onChange={e => setT('signatory', e.target.value)} style={inputStyle} placeholder="Ahmad Al Mansouri" />
          {errors.signatory && <div style={errStyle}>{errors.signatory}</div>}
        </div>
        <div>
          <label style={labelStyle}>Position</label>
          <input value={trade.position} onChange={e => setT('position', e.target.value)} style={inputStyle} placeholder="Managing Director" />
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
        <div>
          <label style={labelStyle}>Email</label>
          <input value={trade.email} onChange={e => setT('email', e.target.value)} style={inputStyle} placeholder="you@company.ae" type="email" />
          {errors.email && <div style={errStyle}>{errors.email}</div>}
        </div>
        <div>
          <label style={labelStyle}>Phone</label>
          <input value={trade.phone} onChange={e => setT('phone', e.target.value)} style={inputStyle} placeholder="+971 50 XXX XXXX" />
          {errors.phone && <div style={errStyle}>{errors.phone}</div>}
        </div>
      </div>
      <div>
        <label style={labelStyle}>Password</label>
        <input value={trade.password} onChange={e => setT('password', e.target.value)} style={inputStyle} placeholder="At least 8 characters" type="password" />
        <div style={{ fontSize: 11, color: INK4, marginTop: 4 }}>Use a mix of letters, numbers and symbols.</div>
        {errors.password && <div style={errStyle}>{errors.password}</div>}
      </div>
    </div>
  );

  const DOC_TYPES = [
    { type: 'trade_license', label: 'Trade license', required: true, desc: 'PDF or image · max 5MB · must be valid' },
    { type: 'emirates_id', label: 'Authorised signatory Emirates ID', required: true, desc: 'Both sides · clear scan' },
    { type: 'vat_certificate', label: 'VAT registration certificate', required: false, desc: 'Optional — required for net pricing' },
    { type: 'memorandum', label: 'Memorandum of Association', required: false, desc: 'Optional — for corporate signatories' },
  ];

  const renderStep3 = () => (
    <div style={{ maxWidth: 560, margin: '0 auto' }}>
      <h2 style={{ fontFamily: heading, fontSize: 28, fontWeight: 700, color: INK, marginBottom: 8 }}>Verification documents</h2>
      <p style={{ fontSize: 14, color: INK3, lineHeight: 1.6, marginBottom: 28 }}>We verify all trade accounts before activation. Documents are encrypted and reviewed within 24 hours.</p>

      {DOC_TYPES.map(dt => {
        const uploaded = docs.find(d => d.type === dt.type);
        return (
          <div key={dt.type} style={{ padding: '16px 20px', border: `1px solid ${LINE}`, borderRadius: 12, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{ width: 36, height: 36, borderRadius: 8, background: uploaded ? '#DCFCE7' : PAPER, display: 'grid', placeItems: 'center', fontSize: 16, flexShrink: 0, color: uploaded ? GREEN : INK4 }}>
              {uploaded ? '✓' : '+'}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: INK, display: 'flex', alignItems: 'center', gap: 8 }}>
                {dt.label}
                {dt.required && <span style={{ fontSize: 10, color: '#DC2626', fontWeight: 700, textTransform: 'uppercase' }}>Required</span>}
              </div>
              <div style={{ fontSize: 12, color: INK4, marginTop: 2 }}>
                {uploaded ? `✅ ${uploaded.name}` : dt.desc}
              </div>
            </div>
            {uploaded ? (
              <button onClick={() => setDocs(prev => prev.filter(d => d.type !== dt.type))} style={{ fontSize: 11, color: '#DC2626', background: 'none', border: 'none', cursor: 'pointer' }}>Remove</button>
            ) : (
              <button onClick={() => fileRefs.current[dt.type]?.click()} style={{ padding: '7px 16px', borderRadius: 8, border: `1px solid ${LINE}`, background: '#fff', fontFamily: heading, fontSize: 12, fontWeight: 500, color: INK, cursor: 'pointer' }}>
                Choose file
              </button>
            )}
            <input ref={el => { fileRefs.current[dt.type] = el; }} type="file" accept=".pdf,.jpg,.jpeg,.png" style={{ display: 'none' }} onChange={e => handleDocUpload(dt.type, e)} />
          </div>
        );
      })}

      {/* Security notice */}
      <div style={{ padding: 16, border: `1px solid ${LINE}`, borderRadius: 12, marginTop: 16, marginBottom: 16, display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <span style={{ fontSize: 16, flexShrink: 0, marginTop: 2 }}>🔒</span>
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, color: INK, marginBottom: 2 }}>Your documents are encrypted</div>
          <div style={{ fontSize: 11, color: INK3, lineHeight: 1.5 }}>Documents stored on UAE servers · accessed only by BigStar verification team · deleted on account closure.</div>
        </div>
      </div>

      {/* Terms */}
      <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 13, color: INK2, cursor: 'pointer' }}>
        <input type="checkbox" checked={termsAgreed} onChange={e => setTermsAgreed(e.target.checked)} style={{ width: 18, height: 18, marginTop: 2, accentColor: BLUE }} />
        <span>I agree to BigStar's <span style={{ color: BLUE, textDecoration: 'underline' }}>Terms of Use</span> and <span style={{ color: BLUE, textDecoration: 'underline' }}>Privacy Policy</span>.</span>
      </label>
    </div>
  );

  const renderStep4 = () => {
    const docCount = docs.length;
    return (
      <div style={{ maxWidth: 560, margin: '0 auto' }}>
        <h2 style={{ fontFamily: heading, fontSize: 28, fontWeight: 700, color: INK, marginBottom: 28 }}>Review & submit</h2>

        {/* Summary rows */}
        <div style={{ borderTop: `1px solid ${LINE}` }}>
          {[
            ['Account', '', type === 'trade' ? 'Business' : 'Individual buyer'],
            ['Type', '', type === 'trade' ? trade.company : ind.name],
            ['Verification status', '', '● Pending review'],
            ...(type === 'trade' ? [
              ['Documents uploaded', '', `${docCount} file${docCount !== 1 ? 's' : ''}`],
            ] : []),
          ].map(([label, , value], i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '14px 0', borderBottom: `1px solid ${LINE}` }}>
              <span style={{ fontFamily: mono, fontSize: 10, letterSpacing: '0.1em', textTransform: 'uppercase', color: INK3 }}>{label}</span>
              <span style={{ fontSize: 13, fontWeight: 500, color: value.includes('Pending') ? '#F59E0B' : INK }}>{value}</span>
            </div>
          ))}
        </div>

        {/* What happens next */}
        <div style={{ marginTop: 24, padding: 24, background: INK, borderRadius: 14, color: '#fff' }}>
          <div style={{ fontFamily: heading, fontSize: 18, fontWeight: 700, marginBottom: 16 }}>What happens next?</div>
          <ol style={{ fontSize: 13, color: 'rgba(255,255,255,0.7)', lineHeight: 2, paddingLeft: 20, margin: 0 }}>
            <li>You'll receive an email confirmation immediately.</li>
            <li>Our team reviews your application{type === 'trade' ? ' and documents' : ''} within 24 hours.</li>
            <li>Once approved, {type === 'trade' ? 'trade pricing & bulk inquiry unlock automatically' : 'you can sign in to browse, save vehicles, and send inquiries'}.</li>
          </ol>
        </div>
      </div>
    );
  };

  return (
    <div style={{ maxWidth: 720, margin: '0 auto', padding: '40px 24px 80px' }}>
      {/* Header */}
      <div style={{ marginBottom: 8 }}>
        <div style={{ fontFamily: mono, fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase', color: BLUE, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ width: 5, height: 5, borderRadius: '50%', background: BLUE }}></span>
          New Account
        </div>
        <h1 style={{ fontFamily: heading, fontSize: 'clamp(32px, 5vw, 48px)', fontWeight: 700, color: INK, letterSpacing: '-0.04em', marginBottom: 12 }}>Register</h1>
        <p style={{ fontSize: 14, color: INK3, lineHeight: 1.6, maxWidth: 480 }}>
          BigStar accounts unlock saved vehicles, inquiry tracking, and — for verified businesses — net pricing.
        </p>
      </div>

      {/* Progress bar */}
      <ProgressBar step={step} type={type || 'trade'} />

      {/* Step content */}
      <div style={{ background: '#fff', borderRadius: 16, border: `1px solid ${LINE}`, padding: '32px 28px' }}>
        {step === 1 && renderStep1()}
        {step === 2 && type === 'individual' && renderStep2Individual()}
        {step === 2 && type === 'trade' && renderStep2Trade()}
        {step === 3 && renderStep3()}
        {step === 4 && renderStep4()}

        {/* Navigation */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 32, paddingTop: 20, borderTop: `1px solid ${LINE}` }}>
          {step === 1 ? (
            <button onClick={() => onGoToLogin ? onGoToLogin() : onNavigate('website-login')} style={{ fontFamily: heading, fontSize: 13, color: INK3, background: 'none', border: 'none', cursor: 'pointer' }}>
              ‹ Back to sign in
            </button>
          ) : (
            <button onClick={back} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '10px 20px', borderRadius: 10, border: `1px solid ${LINE}`, background: '#fff', fontFamily: heading, fontSize: 13, fontWeight: 500, color: INK, cursor: 'pointer' }}>
              ‹ Back
            </button>
          )}
          {step === 4 ? (
            <button onClick={handleSubmit} disabled={submitting} style={{ padding: '12px 28px', borderRadius: 10, background: type === 'trade' ? BLUE : INK, color: '#fff', fontFamily: heading, fontSize: 14, fontWeight: 600, border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
              {submitting ? 'Submitting...' : 'Submit application →'}
            </button>
          ) : (
            <button onClick={next} disabled={step === 1 && !type} style={{ padding: '12px 28px', borderRadius: 10, background: (step === 1 && !type) ? INK4 : INK, color: '#fff', fontFamily: heading, fontSize: 14, fontWeight: 600, border: 'none', cursor: (step === 1 && !type) ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
              Continue <span>→</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default WebsiteRegister;
